import React, { useCallback, useContext, useEffectEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDispatch } from "src/store";
import useTableFetchLoading from "src/ui/hooks/useTableFetchLoading";

import {
    commandsApi,
    useCreateGroupCommandMutation,
    useDeleteGroupCommandMutation,
    useGetGroupCommandsQuery,
} from "@dashboard/devices/api/commands";
import { selectDetailsGroup, selectPaneTab } from "@dashboard/devices/store";
import { MdmCommand } from "@dashboard/devices/types/commands";
import { hasUnfinishedCommands } from "@dashboard/shared/components/SidePanel/shared/CommandTab/CommandTab.utils";
import { skipToken } from "@reduxjs/toolkit/query";
import { showErrorToast, showSuccessToast, showWarningToast } from "@shared/toasts/Toasts";
import { SortingState } from "@tanstack/react-table";

import { commandPollingInterval, emptyCommandList, getSortDirection, rowsPerPage } from "./commandProvider.constants";

function getSortCriteria(sorting: SortingState) {
    if (!sorting.length) {
        return "date";
    }
    const mapping: Record<string, string> = {
        date: "created",
        statistics: "status",
        command: "caption",
    };
    return mapping[sorting[0].id] || "created";
}
type GroupCommandContextType = {
    data: MdmCommand[];
    totalCount: number;
    isLoading: boolean;
    deletingId: number | null;
    resendingId: number | null;
    pageIndex: number;
    sorting: SortingState;
    pageSize: number;
    rowsPerPage: number[];
    onCreate: (json: Record<string, unknown>, commandId: number | null) => Promise<void>;
    onDelete: (command: MdmCommand) => Promise<void>;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
    setPageIndex: React.Dispatch<React.SetStateAction<number>>;
    setPageSize: React.Dispatch<React.SetStateAction<number>>;
};

const GroupCommandContext = React.createContext<GroupCommandContextType>({
    data: [],
    totalCount: 0,
    isLoading: false,
    deletingId: null,
    resendingId: null,
    sorting: [{ id: "date", desc: true }],
    pageIndex: 0,
    pageSize: rowsPerPage[0],
    rowsPerPage,
    onCreate: () => Promise.resolve(),
    onDelete: () => Promise.resolve(),
    setSorting: () => {},
    setPageIndex: () => {},
    setPageSize: () => {},
});

export const GroupCommandProvider = (props: React.PropsWithChildren) => {
    const [sorting, setSorting] = useState<SortingState>([{ id: "date", desc: true }]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(rowsPerPage[0]);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [resendingId, setResendingId] = useState<number | null>(null);

    const { t } = useTranslation(["common", "sidepanels"]);
    const dispatch = useDispatch();
    const group = useSelector(selectDetailsGroup);
    const selectedTab = useSelector(selectPaneTab);
    const [createCommand] = useCreateGroupCommandMutation();
    const [deleteCommand] = useDeleteGroupCommandMutation();

    const groupId = group?.id;
    const isCommandsTab = selectedTab === "COMMANDS";
    const sortCriteria = getSortCriteria(sorting);
    const sortDirection = getSortDirection(sorting);

    const queryArg = useMemo(
        () => (groupId ? { groupId, pageIndex, pageSize, sortCriteria, sortDirection } : undefined),
        [groupId, pageIndex, pageSize, sortCriteria, sortDirection],
    );

    const cachedPage = commandsApi.endpoints.getGroupCommands.useQueryState(queryArg ?? skipToken).data;
    const shouldPoll = hasUnfinishedCommands(cachedPage?.list ?? emptyCommandList) && isCommandsTab;

    const {
        data: commands,
        isFetching,
        isLoading: isInitialLoading,
        refetch,
    } = useGetGroupCommandsQuery(queryArg ?? skipToken, {
        pollingInterval: shouldPoll ? commandPollingInterval : 0,
        skipPollingIfUnfocused: true,
        refetchOnFocus: shouldPoll,
        refetchOnMountOrArgChange: true,
    });

    const data = commands?.list ?? emptyCommandList;
    const totalCount = commands?.count ?? 0;
    const isLoading = useTableFetchLoading(isFetching, isInitialLoading, queryArg ?? null);

    const onCreate = useCallback(
        async (json: Record<string, unknown>, commandId: number | null) => {
            if (!groupId || !queryArg) {
                return;
            }

            try {
                setResendingId(commandId);

                const newCommands = await createCommand({ groupId, json }).unwrap();

                if (!newCommands?.length) {
                    showWarningToast(t("sidepanels:groupPanel.toast.commandNoDevices"));
                    return;
                }

                dispatch(
                    commandsApi.util.updateQueryData("getGroupCommands", queryArg, (draft) => {
                        draft.list.unshift(...newCommands);
                        draft.count += newCommands.length;
                    }),
                );
                showSuccessToast(t("sidepanels:groupPanel.toast.commandQueued", { count: newCommands.length }));
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message || t("sidepanels:groupPanel.toast.commandSentError"));
            } finally {
                setResendingId(null);
            }
        },
        [groupId, queryArg, createCommand, dispatch, t],
    );

    const onDelete = useCallback(
        async (command: MdmCommand) => {
            if (!groupId) {
                return;
            }

            try {
                setDeletingId(command.id);
                await deleteCommand({ groupId, commandId: command.id }).unwrap();
                showSuccessToast(t("sidepanels:groupPanel.toast.commandDeletedSuccess"));
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message || t("sidepanels:groupPanel.toast.commandDeletedError"));
            } finally {
                setDeletingId(null);
            }
        },
        [groupId, deleteCommand, t],
    );

    const wasCommandsTabRef = React.useRef(isCommandsTab);
    const refreshCommands = useEffectEvent(() => {
        void refetch();
    });

    React.useEffect(() => {
        const wasCommandsTab = wasCommandsTabRef.current;
        wasCommandsTabRef.current = isCommandsTab;

        if (!wasCommandsTab && isCommandsTab) {
            refreshCommands();
        }
    }, [isCommandsTab]);

    const contextValue = useMemo(
        () => ({
            data,
            totalCount,
            isLoading,
            deletingId,
            resendingId,
            sorting,
            pageIndex,
            pageSize,
            rowsPerPage,
            onCreate,
            onDelete,
            setSorting,
            setPageIndex,
            setPageSize,
        }),
        [data, totalCount, isLoading, deletingId, resendingId, sorting, pageIndex, pageSize, onCreate, onDelete],
    );

    return <GroupCommandContext.Provider value={contextValue}>{props.children}</GroupCommandContext.Provider>;
};

export const useGroupCommand = () => {
    const context = useContext(GroupCommandContext);
    if (!context) {
        throw new Error("useGroupCommand must be used within a GroupCommandProvider");
    }
    return context;
};
